import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Upload,
  Button,
  Table,
  Switch,
  Select,
  message,
  Alert,
  Space,
  Divider,
  Tag,
  Typography,
} from "antd";
import {
  InboxOutlined,
  WarningOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { createStatement, getAllBanks } from "../../../api/accounting";

const { Dragger } = Upload;
const { Option } = Select;
const { Text } = Typography;

const getDecimalPrecision = () => {
  try { const s = localStorage.getItem("global_decimal_precision"); return s ? parseInt(s, 10) : 3; } catch { return 3; }
};

// Unified hash function for consistent duplicate detection
// MUST match the logic used in all places: preview, save deduplication, and recalculate
const createTransactionHash = (date, description, amount, reference, type) => {
  // Normalize date to YYYY-MM-DD
  let normalizedDate = null;
  if (date) {
    if (typeof date === "number") {
      // Excel date serial number
      const excelEpoch = new Date(1900, 0, 1);
      const dateObj = new Date(excelEpoch.getTime() + (date - 2) * 24 * 60 * 60 * 1000);
      normalizedDate = dayjs(dateObj).format("YYYY-MM-DD");
    } else {
      normalizedDate = dayjs(date).format("YYYY-MM-DD");
    }
  }

  // Normalize amount - use absolute value rounded to 3 decimal places
  const normalizedAmount = Math.round(Math.abs(parseFloat(amount) || 0) * 1000) / 1000;

  // Normalize description - trim and lowercase
  const normalizedDescription = (description || "").trim().toLowerCase();

  // Normalize reference - trim and lowercase
  const normalizedReference = (reference || "").trim().toLowerCase();

  // Normalize type to a simple key (C for credit, D for debit)
  // This ensures transactions with same amount but different types are NOT considered duplicates
  const typeUpper = (type || "").toString().trim().toUpperCase();
  const typeKey = typeUpper.includes("CREDIT") || typeUpper === "C" ? "C" : "D";

  // Create hash including the type to differentiate credits from debits
  let hash = `${normalizedDate}_${normalizedDescription}_${normalizedAmount}_${typeKey}`;
  if (normalizedReference && normalizedReference !== "n/a" && normalizedReference !== "") {
    hash += `_${normalizedReference}`;
  }

  return hash;
};

const UploadBankStatementWithMapping = ({
  visible,
  onCancel,
  onSuccess,
  selectedBrand,
}) => {
  const [step, setStep] = useState(1); // 1: Upload, 2: Map Columns, 3: Preview
  const [fileList, setFileList] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [excelColumns, setExcelColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);

  // Column mapping state - simplified
  const [columnMapping, setColumnMapping] = useState({
    date: { enabled: true, excelColumn: null },
    description: { enabled: true, excelColumn: null },
    type: { enabled: true, excelColumn: null }, // Credit or Debit
    bank_reference: { enabled: true, excelColumn: null }, // ACH_DEBIT, WIRE_OUTGOING, etc.
    amount: { enabled: true, excelColumn: null }, // Transaction amount
    balance: { enabled: false, excelColumn: null }, // Statement balance (optional)
  });

  // Preview data with duplicate detection
  const [previewData, setPreviewData] = useState([]);
  const previewDataRef = useRef([]); // Ref to track latest previewData for save operation
  const [duplicates, setDuplicates] = useState([]);
  const [hasDuplicates, setHasDuplicates] = useState(false);
  const [showOnlyDuplicates, setShowOnlyDuplicates] = useState(false);

  // Keep ref in sync with state
  useEffect(() => {
    previewDataRef.current = previewData;
  }, [previewData]);

  // Column definitions for mapping
  const wafeqColumns = [
    { key: "date", label: "Date", required: true },
    { key: "description", label: "Description", required: true },
    { key: "type", label: "Type (Credit/Debit)", required: true },
    {
      key: "bank_reference",
      label: "Bank Reference (ACH_DEBIT, WIRE, etc.)",
      required: false,
    },
    { key: "amount", label: "Amount", required: true },
    { key: "balance", label: "Statement Balance", required: false },
  ];

  // Fetch banks when modal opens
  useEffect(() => {
    if (visible && selectedBrand) {
      getAllBanks({ brand: selectedBrand.id })
        .then((response) => {
          const banksData = response?.data || response || [];
          setBanks(banksData);
          if (banksData.length === 1) {
            setSelectedBank(banksData[0].id);
          }
        })
        .catch(() => {
          message.error("Failed to fetch banks");
        });
    }
  }, [visible, selectedBrand]);

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      setStep(1);
      setFileList([]);
      setExcelData([]);
      setExcelColumns([]);
      setPreviewData([]);
      setDuplicates([]);
      setHasDuplicates(false);
      setSelectedBank(null);
      setColumnMapping({
        date: { enabled: true, excelColumn: null },
        description: { enabled: true, excelColumn: null },
        type: { enabled: true, excelColumn: null },
        bank_reference: { enabled: true, excelColumn: null },
        amount: { enabled: true, excelColumn: null },
        balance: { enabled: false, excelColumn: null },
      });
    }
  }, [visible]);

  // Handle file upload
  const handleFileUpload = (info) => {
    const { file, fileList: newFileList } = info;

    setFileList(newFileList);

    // Process the file immediately (we use beforeUpload: false, so no status === "done")
    if (file && (file.originFileObj || file instanceof File)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

          if (jsonData.length < 2) {
            message.error(
              "Excel file must have at least a header row and one data row"
            );
            return;
          }

          // First row is headers
          const headers = jsonData[0];
          const rows = jsonData.slice(1).filter((row) => row && row.length > 0);

          setExcelColumns(headers);
          setExcelData(rows);
          setStep(2);
          message.success(
            `File uploaded! Found ${headers.length} columns and ${rows.length} rows.`
          );
        } catch (error) {
          message.error("Failed to read Excel file: " + error.message);
        }
      };
      reader.readAsArrayBuffer(file.originFileObj || file);
    }
  };

  const uploadProps = {
    accept: ".xlsx,.xls",
    beforeUpload: (file) => {
      const isExcel =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel";
      if (!isExcel) {
        message.error("You can only upload Excel files!");
        return Upload.LIST_IGNORE;
      }
      return false; // Prevent auto upload
    },
    onChange: handleFileUpload,
    fileList,
    maxCount: 1,
  };

  // Handle column toggle
  const handleToggle = (columnKey, enabled) => {
    setColumnMapping((prev) => ({
      ...prev,
      [columnKey]: { ...prev[columnKey], enabled },
    }));
  };

  // Handle column selection
  const handleColumnSelect = (columnKey, excelColumn) => {
    setColumnMapping((prev) => ({
      ...prev,
      [columnKey]: { ...prev[columnKey], excelColumn },
    }));
  };

  // Generate preview data
  const handleGeneratePreview = () => {
    // Validate bank selection
    if (!selectedBank) {
      message.error("Please select a bank before previewing");
      return;
    }

    // Validate required mappings
    if (!columnMapping.date.excelColumn) {
      message.error("Date column mapping is required");
      return;
    }

    if (!columnMapping.description.excelColumn) {
      message.error("Description column mapping is required");
      return;
    }

    if (!columnMapping.type.excelColumn) {
      message.error("Type (Credit/Debit) column mapping is required");
      return;
    }

    if (!columnMapping.amount.excelColumn) {
      message.error("Amount column mapping is required");
      return;
    }

    // Map Excel data to our format, ensuring each row has a unique key
    // Use index as key, but also add a unique identifier for duplicates
    const mapped = excelData.map((row, index) => {
      // Create a unique key that includes the index to ensure uniqueness even for duplicates
      const mappedRow = { key: `row-${index}` };

      Object.keys(columnMapping).forEach((key) => {
        const config = columnMapping[key];
        if (config.enabled && config.excelColumn !== null) {
          const colIndex = excelColumns.indexOf(config.excelColumn);
          mappedRow[key] = row[colIndex];
        } else if (config.enabled) {
          mappedRow[key] = null; // Column enabled but not mapped
        }
      });

      return mappedRow;
    });

    // Detect duplicates using unified hash function
    // Use row keys instead of indices for proper duplicate tracking
    const hashToKeys = new Map();
    const duplicateRowKeys = [];

    mapped.forEach((row) => {
      // Use unified hash function for consistent duplicate detection
      const hash = createTransactionHash(
        row.date,
        row.description,
        row.amount,
        row.bank_reference || row.reference,
        row.type
      );
      
      if (!hashToKeys.has(hash)) {
        hashToKeys.set(hash, []);
      }
      hashToKeys.get(hash).push(row.key);
    });

    // Mark all rows with duplicates (first occurrence + subsequent)
    hashToKeys.forEach((keys) => {
      if (keys.length > 1) {
        duplicateRowKeys.push(...keys);
      }
    });

    console.log('[DEBUG] Preview generated:', mapped.length, 'rows');
    console.log('[DEBUG] Duplicate row keys:', duplicateRowKeys.length);
    
    setPreviewData(mapped);
    setDuplicates(duplicateRowKeys);
    setHasDuplicates(duplicateRowKeys.length > 0);
    setShowOnlyDuplicates(false); // Reset filter when generating new preview
    setStep(3); // Move to preview step

    if (duplicateRowKeys.length > 0) {
      message.warning(
        `Found ${duplicateRowKeys.length} duplicate(s). You can review and delete them, or save anyway.`
      );
    } else {
      message.success("No duplicates found! Ready to save.");
    }
  };

  // Handle save
  const handleSave = async () => {
    // Prevent multiple simultaneous saves
    if (loading) {
      return;
    }

    if (!selectedBrand) {
      message.error("Please select a brand first");
      return;
    }

    if (!selectedBank) {
      message.error("Please select a bank");
      return;
    }

    // If duplicates exist, show confirmation with options
    if (hasDuplicates) {
      console.log('[DEBUG] Showing duplicates modal, duplicate count:', duplicates.length);
      Modal.confirm({
        title: "Duplicates Detected",
        content: (
          <div>
            <p>You have <strong>{duplicates.length}</strong> duplicate transaction(s).</p>
            <p>Click <strong>OK</strong> to save ALL transactions (including duplicates).</p>
            <p>Click <strong>Cancel</strong> to go back and review.</p>
          </div>
        ),
        okText: "Save All (Keep Duplicates)",
        cancelText: "Cancel",
        onOk: async () => {
          // User wants to keep ALL duplicates - skip deduplication entirely
          console.log('[DEBUG] User clicked Save All - calling performSave(true)');
          await performSave(true);
        },
      });
      return;
    }

    await performSave(false);
  };

  // Perform the actual save operation
  // skipDeduplication: if true, skip deduplication (user explicitly chose to keep all)
  const performSave = async (skipDeduplication = false) => {
    setLoading(true);

    try {
      // Use ref to get the absolute latest previewData (including any deletions)
      const currentPreviewData =
        previewDataRef.current.length > 0
          ? previewDataRef.current
          : previewData;

      console.log('[DEBUG] currentPreviewData count:', currentPreviewData.length);
      console.log('[DEBUG] previewDataRef.current count:', previewDataRef.current?.length);
      console.log('[DEBUG] previewData count:', previewData?.length);

      if (currentPreviewData.length === 0) {
        message.error(
          "Cannot save: No transactions to save. Please add at least one transaction."
        );
        setLoading(false);
        return;
      }

      // Normalization helpers
      const NORMALIZE_TYPE = (raw, fallbackDetail) => {
        const t = String(raw || "")
          .trim()
          .toUpperCase();
        // Map common bank exports to our allowed TYPE_CHOICES
        const map = {
          // ACH
          ACH_DEBIT: "ACH_DEBIT",
          "ACH CREDIT": "ACH_CREDIT",
          ACH_CREDIT: "ACH_CREDIT",
          // Wires
          WIRE_OUT: "WIRE_OUTGOING",
          WIRE_OUTGOING: "WIRE_OUTGOING",
          OUTGOING_WIRE: "WIRE_OUTGOING",
          WIRE_IN: "WIRE_INCOMING",
          WIRE_INCOMING: "WIRE_INCOMING",
          INCOMING_WIRE: "WIRE_INCOMING",
          WIRE: fallbackDetail === "DEBIT" ? "WIRE_OUTGOING" : "WIRE_INCOMING",
          WIRE_RETURN:
            fallbackDetail === "DEBIT" ? "WIRE_OUTGOING" : "WIRE_INCOMING",
          // Checks
          CHECK_PAID: "CHECK_PAID",
          CHECK: fallbackDetail === "DEBIT" ? "CHECK_PAID" : "CHECK_DEPOSIT",
          CHECK_DEPOSIT: "CHECK_DEPOSIT",
          DEPOSITED_CHECK: "CHECK_DEPOSIT",
          // Transfers / fees / misc
          ACCT_XFER: "ACCT_XFER",
          ACCOUNT_TRANSFER: "ACCT_XFER",
          TRANSFER: "ACCT_XFER",
          FEE: "FEE_TRANSACTION",
          FEE_TRANSACTION: "FEE_TRANSACTION",
          DEPOSIT_RETURN: "DEPOSIT_RETURN",
          ADJUSTMT_REVERSAL: "ADJUSTMT_REVERSAL",
          DEPOSIT: "DEPOSIT",
          MISC_DEBIT: "MISC_DEBIT",
          DEBIT_CARD: "DEBIT_CARD",
          // Raw terms that often appear
          WITHDRAWAL: "ACH_DEBIT",
          DEPOSITED: "ACH_CREDIT",
          CREDIT: fallbackDetail === "DEBIT" ? "ACH_DEBIT" : "ACH_CREDIT",
          DEBIT: fallbackDetail === "CREDIT" ? "ACH_CREDIT" : "ACH_DEBIT",
        };
        if (map[t]) return map[t];
        // If we receive "CREDIT"/"DEBIT" or unknown, fall back to ACH_* by detail
        if (t === "CREDIT" || t === "DEBIT" || !t) {
          return fallbackDetail === "DEBIT" ? "ACH_DEBIT" : "ACH_CREDIT";
        }
        // Try bank_reference keywords
        if (t.includes("WIRE")) {
          return t.includes("IN") ? "WIRE_INCOMING" : "WIRE_OUTGOING";
        }
        if (t.includes("CHECK") && t.includes("DEPOSIT"))
          return "CHECK_DEPOSIT";
        if (t.includes("CHECK") && t.includes("PAID")) return "CHECK_PAID";
        if (t.includes("TRANSFER")) return "ACCT_XFER";
        if (t.includes("FEE")) return "FEE_TRANSACTION";
        if (t.includes("DEPOSIT")) return "DEPOSIT";
        if (t.includes("REVERSAL")) return "ADJUSTMT_REVERSAL";
        // Default to ACH_* based on detail
        return fallbackDetail === "DEBIT" ? "ACH_DEBIT" : "ACH_CREDIT";
      };

      const NORMALIZE_DETAIL = (rawType, rawBankRef, amount) => {
        // Always let amount sign be the final arbiter if available
        const byAmount = Number(amount) < 0 ? "DEBIT" : "CREDIT";
        const t = String(rawType || "")
          .trim()
          .toUpperCase();
        const b = String(rawBankRef || "")
          .trim()
          .toUpperCase();
        // Direct terms
        if (t === "DEBIT" || b === "DEBIT" || t.includes("DEBIT"))
          return "DEBIT";
        if (t === "CREDIT" || b === "CREDIT" || t.includes("CREDIT"))
          return "CREDIT";
        // Wire
        if (b.includes("WIRE_OUT") || b === "WIRE_OUTGOING") return "DEBIT";
        if (b.includes("WIRE_IN") || b === "WIRE_INCOMING") return "CREDIT";
        if (b === "WIRE_RETURN") return byAmount;
        // Checks
        if (b === "CHECK_PAID") return "DEBIT";
        if (b === "CHECK_DEPOSIT") return "CREDIT";
        // Deposit / fees
        if (b === "DEPOSIT" || b === "DEPOSIT_RETURN") return byAmount;
        if (b === "FEE_TRANSACTION" || b.includes("FEE")) return "DEBIT";
        // Transfers/misc
        if (b === "ACCT_XFER" || b.includes("TRANSFER")) return byAmount;
        if (b === "MISC_DEBIT" || b === "DEBIT_CARD") return "DEBIT";
        // Fallback to amount
        return byAmount;
      };

      // CRITICAL: Only process rows that are still in previewData (deleted rows are already filtered out)
      const parseStatementNumber = (value) => {
        if (value === null || value === undefined || value === "") return null;
        if (typeof value === "number") return value;
        const cleaned = String(value).replace(/[$,\s]/g, "");
        const parsed = parseFloat(cleaned);
        return Number.isNaN(parsed) ? null : parsed;
      };

      const transactions = currentPreviewData.map((row) => {
        // Get amount from the amount column
        let amount = parseFloat(row.amount) || 0;

        // Get detail (CREDIT/DEBIT) and type (ACH_CREDIT, ACH_DEBIT, etc.)
        // Handle both cases: Excel might have detailed types in 'type' or simple CREDIT/DEBIT
        let detail, transactionType;

        // Check if type contains a detailed transaction type (ACH_DEBIT, ACH_CREDIT, etc.)
        const detailedTransactionTypes = [
          "ACH_DEBIT",
          "ACH_CREDIT",
          "WIRE_OUTGOING",
          "WIRE_INCOMING",
          "CHECK_PAID",
          "CHECK_DEPOSIT",
          "ACCT_XFER",
          "FEE_TRANSACTION",
          "DEPOSIT_RETURN",
          "ADJUSTMT_REVERSAL",
          "DEPOSIT",
          "MISC_DEBIT",
          "DEBIT_CARD",
        ];

        const typeUpper = row.type ? row.type.toUpperCase() : "";
        const isDetailedType = detailedTransactionTypes.includes(typeUpper);

        if (isDetailedType) {
          // Normalize detail from the detailed type and amount
          const tmpDetail = NORMALIZE_DETAIL(
            typeUpper,
            row.bank_reference,
            amount
          );
          detail = tmpDetail;
          transactionType = NORMALIZE_TYPE(typeUpper, detail);
        } else {
          // Excel 'type' column contains simple CREDIT/DEBIT; normalize with amount and bank reference
          const tmpDetail = NORMALIZE_DETAIL(
            typeUpper,
            row.bank_reference,
            amount
          );
          detail = tmpDetail;
          transactionType = NORMALIZE_TYPE(row.bank_reference, detail);
        }

        // Convert Excel date serial numbers to proper dates
        let transactionDate = null;
        if (row.date) {
          if (typeof row.date === "number") {
            // Excel date serial number
            const excelEpoch = new Date(1900, 0, 1);
            const date = new Date(
              excelEpoch.getTime() + (row.date - 2) * 24 * 60 * 60 * 1000
            );
            transactionDate = dayjs(date).format("YYYY-MM-DD");
          } else {
            // Already a date string or object
            transactionDate = dayjs(row.date).format("YYYY-MM-DD");
          }
        }

        // IMPORTANT: Use consistent field ordering - bank_reference takes priority
        // This matches the preview duplicate detection logic exactly
        const referenceValue = row.bank_reference || row.reference || "N/A";
        const balance = parseStatementNumber(row.balance);

        return {
          transaction_date: transactionDate,
          description: (row.description || "").trim(), // Normalize description (trim whitespace)
          reference: referenceValue,
          bank_reference: referenceValue !== "N/A" ? referenceValue : null, // Keep for deduplication
          detail, // CREDIT or DEBIT
          type: transactionType, // ACH_DEBIT, WIRE_OUTGOING, etc.
          amount: amount.toString(),
          balance: balance !== null ? balance.toString() : null,
          notes: null,
          duplicate_status: "NEW_ENTRY",
        };
      });

      // Debug: Log what we're about to save
      console.log('[DEBUG] performSave called with skipDeduplication:', skipDeduplication);
      console.log('[DEBUG] Number of transactions to process:', transactions.length);
      
      // Deduplicate transactions before saving (unless user chose to keep all)
      let finalTransactions = transactions;
      
      if (!skipDeduplication) {
        const seenTransactions = new Set();
        const uniqueTransactions = [];

        transactions.forEach((transaction) => {
          // Use unified hash function - matches preview detection exactly
          const signature = createTransactionHash(
            transaction.transaction_date,
            transaction.description,
            transaction.amount,
            transaction.bank_reference || transaction.reference,
            transaction.detail // Use detail (CREDIT/DEBIT) for type
          );

          if (!seenTransactions.has(signature)) {
            seenTransactions.add(signature);
            uniqueTransactions.push(transaction);
          }
        });

        const duplicateCount = transactions.length - uniqueTransactions.length;
        if (duplicateCount > 0) {
          message.info(
            `Removed ${duplicateCount} duplicate transaction(s) before saving`
          );
        }

        finalTransactions = uniqueTransactions;
      }
      
      console.log('[DEBUG] Final transactions to save:', finalTransactions.length);

      // Generate file hash for the uploaded file (max 32 chars)
      // Include timestamp to ensure uniqueness even for same file
      let fileHash = null;
      if (fileList[0]?.originFileObj) {
        // For file uploads, generate hash based on file content + timestamp
        const file = fileList[0].originFileObj;
        const hashInput = `${file.name}_${file.size}_${
          file.lastModified
        }_${selectedBank}_${Date.now()}`;
        let hash = 0;
        for (let i = 0; i < hashInput.length; i++) {
          const char = hashInput.charCodeAt(i);
          hash = (hash << 5) - hash + char;
          hash = hash & hash; // Convert to 32bit integer
        }
        // Ensure hash is within 32 character limit
        const hexHash = Math.abs(hash).toString(16).padStart(8, "0");
        fileHash = hexHash.substring(0, 32);
      } else {
        // For manual entry without file, generate unique hash
        const hashInput = `${Date.now()}_${selectedBank}_${dayjs().format(
          "YYYY-MM-DD"
        )}_${transactions.length}_${Math.random()}`;
        let hash = 0;
        for (let i = 0; i < hashInput.length; i++) {
          const char = hashInput.charCodeAt(i);
          hash = (hash << 5) - hash + char;
          hash = hash & hash; // Convert to 32bit integer
        }
        // Ensure hash is within 32 character limit
        const hexHash = Math.abs(hash).toString(16).padStart(8, "0");
        fileHash = hexHash.substring(0, 32);
      }

      // Create statement with transactions
      const statementData = {
        brand: selectedBrand.id,
        bank: selectedBank,
        statement_date: dayjs().format("YYYY-MM-DD"),
        start_date: dayjs().subtract(30, "days").format("YYYY-MM-DD"),
        end_date: dayjs().format("YYYY-MM-DD"),
        opening_balance: 0,
        closing_balance:
          currentPreviewData[currentPreviewData.length - 1]?.balance || 0,
        file_hash: fileHash,
        transactions: finalTransactions,
        // Tell backend to skip its deduplication if user chose to keep duplicates
        skip_deduplication: skipDeduplication,
      };

      await createStatement(
        statementData,
        fileList[0]?.originFileObj
      );

      message.success("Bank statement uploaded successfully!");

      if (onSuccess) {
        onSuccess();
      }

      onCancel();
    } catch (error) {
      message.error(
        "Failed to save statement: " +
          (error.response?.data?.error || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  // Render upload step
  const renderUploadStep = () => (
    <div>
      <Dragger {...uploadProps}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag Excel file to upload</p>
        <p className="ant-upload-hint">
          Upload your bank statement in Excel format (.xlsx, .xls)
        </p>
      </Dragger>
    </div>
  );

  // Render column mapping step
  const renderMappingStep = () => (
    <div>
      <Alert
        message="Map columns from your Excel file to DataVision fields"
        description="Toggle columns on/off and select the matching column from your file"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Bank Selection */}
      <div style={{ marginBottom: 16 }}>
        <Text strong>Select Bank: </Text>
        <Select
          placeholder="Select bank account"
          style={{ width: "100%", marginTop: 8 }}
          value={selectedBank}
          onChange={setSelectedBank}
        >
          {banks.map((bank) => (
            <Option key={bank.id} value={bank.id}>
              {bank.name} - {bank.account_number}
            </Option>
          ))}
        </Select>
      </div>

      {/* Debug Info */}
      {excelColumns.length > 0 && (
        <Alert
          message={`Found ${excelColumns.length} columns in your Excel file`}
          description={`Columns: ${excelColumns.join(", ")}`}
          type="success"
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <Divider />

      <Table
        dataSource={wafeqColumns}
        pagination={false}
        rowKey="key"
        size="small"
        columns={[
          {
            title: "Column name in DataVision",
            dataIndex: "label",
            key: "label",
            width: 250,
            render: (text, record) => (
              <Space>
                <Switch
                  checked={columnMapping[record.key].enabled}
                  onChange={(checked) => handleToggle(record.key, checked)}
                  size="small"
                />
                <Text strong={record.required}>{text}</Text>
                {record.required && <Tag color="red">Required</Tag>}
              </Space>
            ),
          },
          {
            title: "→",
            width: 50,
            align: "center",
          },
          {
            title: "Column name in your file",
            key: "mapping",
            render: (text, record) => (
              <Select
                placeholder="Select..."
                style={{ width: "100%" }}
                value={columnMapping[record.key].excelColumn}
                onChange={(value) => handleColumnSelect(record.key, value)}
                disabled={!columnMapping[record.key].enabled}
                allowClear
              >
                {excelColumns.map((col, idx) => (
                  <Option key={idx} value={col}>
                    {col}
                  </Option>
                ))}
              </Select>
            ),
          },
        ]}
      />

      <Divider />

      <Space>
        <Button onClick={() => setStep(1)}>Back</Button>
        <Button type="primary" onClick={handleGeneratePreview}>
          Preview Import
        </Button>
      </Space>
    </div>
  );

  // Helper function to recalculate duplicates from preview data
  const recalculateDuplicates = (previewRows) => {
    const hashToKeys = new Map();
    const duplicateRowKeys = [];

    previewRows.forEach((row) => {
      // Use unified hash function for consistent duplicate detection
      const hash = createTransactionHash(
        row.date,
        row.description,
        row.amount,
        row.bank_reference || row.reference,
        row.type
      );
      
      if (!hashToKeys.has(hash)) {
        hashToKeys.set(hash, []);
      }
      // Use the row's key (which is unique) instead of index
      hashToKeys.get(hash).push(row.key);
    });

    // Mark all rows with duplicates (first occurrence + subsequent)
    hashToKeys.forEach((keys) => {
      if (keys.length > 1) {
        duplicateRowKeys.push(...keys);
      }
    });

    return duplicateRowKeys;
  };

  // Handle delete row from preview
  const handleDeleteRow = (rowKey) => {
    if (loading) {
      message.warning("Please wait for the current operation to complete");
      return;
    }

    setPreviewData((currentPreview) => {
      const rowToDelete = currentPreview.find((r) => r.key === rowKey);
      if (!rowToDelete) {
        message.error("Row not found for deletion");
        return currentPreview;
      }

      if (currentPreview.length <= 1) {
        message.warning("Cannot delete the last row. At least one transaction is required.");
        return currentPreview;
      }

      const updatedPreview = currentPreview.filter((row) => row.key !== rowKey);

      // Update ref immediately so save operation has latest data
      previewDataRef.current = updatedPreview;

      // Recalculate duplicates synchronously
      const duplicateRowKeys = recalculateDuplicates(updatedPreview);
      
      // Update duplicate state after previewData update
      setTimeout(() => {
        setDuplicates(duplicateRowKeys);
        setHasDuplicates(duplicateRowKeys.length > 0);
      }, 0);

      return updatedPreview;
    });

    message.success("Row deleted successfully");
  };

  // Render preview step
  const renderPreviewStep = () => {
    const previewColumns = [
      {
        title: "Row",
        dataIndex: "key",
        key: "row",
        width: 60,
        fixed: "left",
        render: (key, record, index) => {
          // Extract row number from key (e.g., "row-0" -> 1, "row-1" -> 2)
          const match = key?.match(/row-(\d+)/);
          return match ? parseInt(match[1]) + 1 : index + 1;
        },
      },
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        width: 120,
        render: (value) => {
          if (!value) return "-";
          // Handle Excel date serial numbers
          if (typeof value === "number") {
            const excelEpoch = new Date(1900, 0, 1);
            const date = new Date(
              excelEpoch.getTime() + (value - 2) * 24 * 60 * 60 * 1000
            );
            return dayjs(date).format("YYYY-MM-DD");
          }
          return dayjs(value).format("YYYY-MM-DD");
        },
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        width: 250,
        render: (value) => value || "-",
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        width: 100,
        render: (value) => {
          const type = value ? value.toUpperCase() : "-";
          return (
            <Tag
              color={
                type === "CREDIT"
                  ? "green"
                  : type === "DEBIT"
                  ? "red"
                  : "default"
              }
            >
              {type}
            </Tag>
          );
        },
      },
      {
        title: "Bank Reference",
        dataIndex: "bank_reference",
        key: "bank_reference",
        width: 150,
        render: (value) => value || "-",
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        width: 120,
        render: (value) => {
          const num = parseFloat(value) || 0;
          return (
            <span
              style={{ color: num < 0 ? "red" : "green", fontWeight: "500" }}
            >
              ${num.toFixed(getDecimalPrecision())}
            </span>
          );
        },
      },
      {
        title: "Balance",
        dataIndex: "balance",
        key: "balance",
        width: 120,
        render: (value) => (value ? `$${parseFloat(value).toFixed(getDecimalPrecision())}` : "-"),
      },
      {
        title: "Status",
        key: "status",
        width: 100,
        render: (text, record) => {
          const isDuplicate = duplicates.includes(record.key);
          return isDuplicate ? (
            <Tag color="error" icon={<WarningOutlined />}>
              Duplicate
            </Tag>
          ) : (
            <Tag color="success">OK</Tag>
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        width: 80,
        fixed: "right",
        render: (text, record) => (
          <Button
            type="text"
            danger
            size="small"
            disabled={loading}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDeleteRow(record.key);
            }}
          >
            Delete
          </Button>
        ),
      },
    ];

    return (
      <div>
        {hasDuplicates && (
          <Alert
            message="Duplicates Detected!"
            description={
              <div>
                <p>
                  Found {duplicates.length} duplicate transaction(s). You can
                  review and delete duplicates if needed, or save anyway.
                </p>
                <p style={{ margin: 0, fontWeight: "bold" }}>
                  Look for rows with red &quot;Duplicate&quot; tags in the
                  Status column.
                </p>
              </div>
            }
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <Text type="secondary">
            Preview of {previewData.length} transactions
            {showOnlyDuplicates && (
              <span style={{ marginLeft: 8 }}>
                (Showing {duplicates.length} duplicates)
              </span>
            )}
          </Text>
          {hasDuplicates && (
            <Button
              icon={<FilterOutlined />}
              onClick={() => setShowOnlyDuplicates(!showOnlyDuplicates)}
              type={showOnlyDuplicates ? "primary" : "default"}
            >
              {showOnlyDuplicates ? "Show All" : "Show Only Duplicates"}
            </Button>
          )}
        </div>

        <Table
          dataSource={
            showOnlyDuplicates
              ? previewData.filter((row) => duplicates.includes(row.key))
              : previewData
          }
          columns={previewColumns}
          pagination={{ 
            pageSize: 50, 
            showSizeChanger: true, 
            pageSizeOptions: ['10', '25', '50', '100', '200'],
            showTotal: (total) => `Total ${total} transactions`
          }}
          scroll={{ x: 1200, y: 400 }}
          size="small"
          style={{ marginTop: 16 }}
          rowClassName={(record) =>
            duplicates.includes(record.key) ? "duplicate-row" : ""
          }
        />

        <Divider />

        <Space>
          <Button onClick={() => setStep(2)}>Back to Mapping</Button>
          <Button type="primary" onClick={handleSave} loading={loading}>
            Save Statement
            {hasDuplicates && (
              <span style={{ marginLeft: 8, fontWeight: "normal" }}>
                (with duplicates)
              </span>
            )}
          </Button>
        </Space>
      </div>
    );
  };

  return (
    <Modal
      title={
        step === 1
          ? "Upload Bank Statement"
          : step === 2
          ? "Map Columns"
          : "Preview Import"
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={step === 3 ? 1400 : 800}
      destroyOnClose
      centered
      zIndex={2000}
      style={{ top: 20 }}
      bodyStyle={{ maxHeight: "85vh", overflowY: "auto" }}
    >
      <style>
        {`
          .duplicate-row {
            background-color: #fff1f0 !important;
            border-left: 4px solid #ff4d4f !important;
          }
          .duplicate-row:hover > td {
            background-color: #ffe7e5 !important;
          }
        `}
      </style>

      {step === 1 && renderUploadStep()}
      {step === 2 && renderMappingStep()}
      {step === 3 && renderPreviewStep()}
    </Modal>
  );
};

export default UploadBankStatementWithMapping;
